import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import * as queryString from 'query-string';
import { stockCarIdVar, selectedFeaturesInputVar } from '../../../gqlCommon/graphql/cache';
import formatDisplayFeaturesService from '../../../gqlStock/service/formatDisplayFeaturesService';
import ITransferGroup from '../../../gqlStock/model/ITransferGroup';
import useStockCar from './useStockCar';
import { GetCustomizeCar_customizeCar_similarStockCars } from '../../graphql/queries/__generated__/GetCustomizeCar';
import useStockCars from './useStockCars';
import urlRoute from '../../../common/service/urlRoute';
import { ROUTES } from '../../../common/constants/routes';
import { StockFeatureType } from '../../enum/StockFeatureType';
import IParams from '../../model/IParams';
import useCustomizeCarContext from '../useCustomizeCarContext';

export const useTransfer = () => {
  const { selectedCar, loading } = useCustomizeCarContext();
  const carName = selectedCar?.car?.attributes?.vehicleDisplayName ?? '';
  const [carImageUrl, setCarImageUrl] = useState<string>('');
  const elements = useStockCars();
  const { getStockCarByCarId } = useStockCar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nextStockData, setNextStockData] = useState<GetCustomizeCar_customizeCar_similarStockCars[]>(null);
  const [validGroup, setValidGroup] = useState<ITransferGroup>(null);
  const [invalidGroup, setInvalidGroup] = useState<ITransferGroup>(null);
  const [carId, setCarId] = useState<string>('');
  const history = useHistory();
  const { trimCode } = useParams<IParams>();
  const {
    location: { search },
  } = history;
  const searchQuery = new URLSearchParams(search);
  searchQuery.delete(StockFeatureType.oldFeatures);
  const selectedFeatureOptions = queryString.parse(`?${searchQuery}`);
  const navigationPath = urlRoute.createUrl(
    ROUTES.CONFIGURE_GQL,
    {
      trimCode: decodeURIComponent(trimCode),
      externalId: selectedCar?.car?.code,
    },
    selectedFeatureOptions,
  );
  const { selected, added, removed } = selectedFeaturesInputVar();
  const [prevSelected, setPrevSelected] = useState<string[]>([]);
  const [prevAdded, setPrevAdded] = useState<string[]>([]);
  const [prevRemoved, setPrevRemoved] = useState<string[]>([]);

  useEffect(() => {
    if (!loading && selectedCar) {
      const { validGroup, invalidGroup } = formatDisplayFeaturesService.getTransferGroup(selectedCar);
      setValidGroup(validGroup);
      setInvalidGroup(invalidGroup);
    }
  }, [selectedCar]);

  useEffect(() => {
    if (selectedCar && !loading && isModalOpen) {
      setCarImageUrl(selectedCar.images[0].url);
    }
  }, [selectedCar, isModalOpen]);

  useEffect(() => {
    setPrevSelected(selected);
    setPrevAdded(added);
    setPrevRemoved(removed);
  }, []);

  const handleToggleModal = useCallback(() => {
    stockCarIdVar(null);
    setIsModalOpen(prev => !prev);
  }, []);

  const handleOnAcceptTransfer = useCallback(() => {
    let stock;
    if (elements.length) {
      stock = elements.find(element => element.carId === carId).onSelectCar;
      stockCarIdVar(null);
      setIsModalOpen(false);
    }

    return stock();
  }, [loading]);

  const handleStockAcceptTransfer = useCallback(() => {
    history.push(navigationPath);
  }, []);

  const handleStockDeclineTransfer = useCallback(() => {
    selectedFeaturesInputVar({
      selected: prevSelected,
      added: prevAdded,
      removed: prevRemoved,
    });
    history.push(navigationPath);
  }, [prevSelected, prevAdded, prevRemoved]);

  const handleTransferModalSelect = useCallback(
    (carId: string) => {
      stockCarIdVar(carId);

      if (!loading) {
        setCarId(carId);
        const stockCarData = getStockCarByCarId(carId);
        setNextStockData(stockCarData);
        setIsModalOpen(true);
      }
    },
    [loading, getStockCarByCarId],
  );

  return {
    loading,
    carName,
    carImageUrl,
    validGroup,
    invalidGroup,
    isModalOpen,
    nextStockData,
    handleToggleModal,
    handleOnAcceptTransfer,
    handleTransferModalSelect,
    handleStockAcceptTransfer,
    handleStockDeclineTransfer,
  };
};
